import math from './customMath';

class Trinome {
    private a: number;
    private b: number;
    private c: number;

    constructor(a: number, b: number, c: number) {
        this.a = math.simplify(`${a}`).evaluate();
        this.b = math.simplify(`${b}`).evaluate();
        this.c = math.simplify(`${c}`).evaluate();
    }

    public discriminant(): math.MathNode {
        const deltaExpression = `(${this.b})^2 - 4 * ${this.a} * ${this.c}`;
        const delta = math.simplify(deltaExpression);
        return delta;
    }

    public racines(): { x1: math.MathNode, x2: math.MathNode } | string {
        const delta = this.discriminant().evaluate();
        const zero = math.number(0);

        if (math.compare(delta, zero) === 1) { // Δ > 0
            const x1Expression = `(-${this.b} - sqrt(${delta})) / (2 * ${this.a})`;
            const x2Expression = `(-${this.b} + sqrt(${delta})) / (2 * ${this.a})`;

            const x1 = math.simplify(x1Expression);
            const x2 = math.simplify(x2Expression);

            return { x1, x2 };
        } else if (math.compare(delta, zero) === 0) { // Δ = 0
            const x1Expression = `(-${this.b}) / (2 * ${this.a})`;
            const x1 = math.simplify(x1Expression);
            return { x1, x2: x1 };
        } else { // Δ < 0
            return "No real roots";
        }
    }
    // Donner la forme développée du trinôme
    public formeDeveloppee(): math.MathNode {
        const rules = [
            { l: 'n1+-n2', r: 'n1-n2' },
            { l: 'n1+-n2/n3*n4', r: 'n1-n2/n3*n4' },
            { l: 'n1-n2*v^2', r: '-n2*v^2+n1' },
            { l: 'n1*v^2+n2-n3*v', r: 'n1*v^2-n3*v+n2' },
            { l: 'v^2+n2-n3*v', r: 'v^2-n3*v+n2' },
            { l: '1*n', r: 'n' },
            { l: 'n*1', r: 'n' },
            { l: 'n1*n2', r: 'n1 n2' },
        ]
        const formeDeveloppeeExpression = `${this.a} * x^2 + ${this.b} * x + ${this.c}`;
        const formeDeveloppee = math.simplify(formeDeveloppeeExpression, rules);
        return formeDeveloppee;
    }
    // À partir d'une expression, la développer et affecter les valeurs de a, b et c
    public setFromExpression(expression: string): void {
        //const rulesDeveloppement = math.simplify.rules.concat([{l:'n1*(n2+n3)',r:'n1*n2+n1*n3'},{l:'(n2+n3)*n1',r:'n1*n2+n1*n3'}]);
        //const rulesDeveloppement = math.simplify.rules.concat([{l:'n1*(n2+n3)',r:'n1*n2+n1*n3'},{l:'(n2+n3)*n1',r:'n1*n2+n1*n3'},{l:'n1*(n2-n3)',r:'n1*n2+-n1*n3'}]);
        const rulesDeveloppement = math.simplify.rules.concat([{ l: 'n1*(n2+n3)', r: 'n1*n2+n1*n3' }, { l: '(n2+n3)*n1', r: 'n1*n2+n1*n3' }, { l: 'n1*(n2-n3)', r: 'n1*n2+-n1*n3' }, { l: '(n2-n3)*n1', r: 'n1*n2+-n1*n3' }]);
        let formeDeveloppee = math.simplify(expression,rulesDeveloppement);
        const rulesTermesOrdonnes = [
            { l: 'n1+-n2', r: 'n1-n2' },
            { l: 'n1+-n2/n3*n4', r: 'n1-n2/n3*n4' },
            { l: 'n1-n2*v^2', r: '-n2*v^2+n1' },
            { l: 'n1+n2*v^2', r: 'n2*v^2+n1' },
            { l: 'n+v^2', r: 'v^2+n' },
            { l: 'n-v^2', r: '-v^2+n' },
            { l: 'n1*v^2+n2-n3*v', r: 'n1*v^2-n3*v+n2' },
            { l: 'v^2+n2-n3*v', r: 'v^2-n3*v+n2' },
            { l: '1*n', r: 'n' },
            { l: 'n*1', r: 'n' },
            { l: 'n1*n2', r: 'n1 n2' },
        ]
        const formeDeveloppeeString = math.simplify(formeDeveloppee,rulesTermesOrdonnes).toString().replace(/ /g,'');
        const termeCarre = formeDeveloppeeString.split('x^2')[0];
        if (termeCarre === '') {
            this.a = math.simplify('1').evaluate();
        } else {
            this.a = math.simplify(termeCarre).evaluate();
        }
        const termeLineaire = formeDeveloppeeString.split('x^2')[1].split('x');
        if (termeLineaire.length > 1) {
            if (termeLineaire[0] === '') {
                this.b = math.simplify('0').evaluate();
            }
            else if (termeLineaire[0] === '-') {
                this.b = math.simplify('-1').evaluate();
            } else if (termeLineaire[0] === '+') {
                this.b = math.simplify('1').evaluate();
            } else {
                this.b = math.simplify(termeLineaire[0]).evaluate();
            }
        } else {
            this.b = math.simplify('0').evaluate();
        }
        const termeConstant = formeDeveloppeeString.split('x^2')[1].split('x');
        if (termeConstant.length > 1) {
            this.c = math.simplify(termeConstant[1]).evaluate();
        } else {
            if (termeConstant[0] === '') {
                this.c = math.simplify('0').evaluate();
            } else {
                this.c = math.simplify(termeConstant[0]).evaluate();
            }
        }
    }
    // À partir de deux racines, affecter les valeurs de a, b et c
    public setFromRacines(x1: string, x2: string): void {
        this.a = math.parse('1').evaluate();
        this.b = math.simplify(`-${x1} - ${x2}`).evaluate();
        this.c = math.simplify(`${x1} * ${x2}`).evaluate();
    }

    // Forme canonique
    public formeCanonique(): math.MathNode {
        const hExpression = `-${this.b} / (2 * ${this.a})`;
        const h = math.simplify(hExpression);
        const kExpression = `-${this.discriminant()} / (4 * ${this.a})`;
        const k = math.simplify(kExpression);
        // Forme canonique à retourner ua format MathNode : a * (x - h)^2 + k
        const formeCanoniqueExpression = `${this.a} * (x - (${h}))^2 + (${k})`;
        const formeCanonique = math.simplify(formeCanoniqueExpression);
        return formeCanonique;
    }

    // Forme factorisée
    public formeFactorisee(): math.MathNode | string {
        const delta = this.discriminant().evaluate();
        const zero = math.number(0);
        if (math.compare(delta, zero) === 1) { // Δ > 0
            const x1Expression = `(-${this.b} - sqrt(${delta})) / (2 * ${this.a})`;
            const x2Expression = `(-${this.b} + sqrt(${delta})) / (2 * ${this.a})`;

            const x1 = math.simplify(x1Expression);
            const x2 = math.simplify(x2Expression);

            const formeFactoriseeExpression = `${this.a} * (x - (${x1})) * (x - (${x2}))`;
            const formeFactorisee = math.simplify(formeFactoriseeExpression);
            return formeFactorisee;
        } else if (math.compare(delta, zero) === 0) { // Δ = 0
            const x1Expression = `(-${this.b}) / (2 * ${this.a})`;
            const x1 = math.simplify(x1Expression);
            const formeFactoriseeExpression = `${this.a} * (x - (${x1}))^2`;
            const formeFactorisee = math.simplify(formeFactoriseeExpression);
            return formeFactorisee;
        } else { // Δ < 0
            return "No real roots";
        }
    }
    public inegalite(): string {
        const delta = this.discriminant().evaluate();
        const zero = math.number(0);
        const aNumber = Number(this.a.valueOf());
        if (math.compare(delta, zero) === 1) { // Δ > 0
            const x1Expression = math.simplify(`(-${this.b} - sqrt(${delta})) / (2 * ${this.a})`);
            const x2Expression = math.simplify(`(-${this.b} + sqrt(${delta})) / (2 * ${this.a})`);

            let x1, x2;
            if (x1Expression.valueOf() < x2Expression.valueOf()) {
                x1 = x1Expression.toTex();
                x2 = x2Expression.toTex();
            } else {
                x1 = x2Expression.toTex();
                x2 = x1Expression.toTex();
            }
            if (aNumber > 0) {
                return `\\left]-\\infty;${x1}\\right[ \\cup \\left]${x2};+\\infty\\right[`;
            } else {
                return `\\left]${x1};${x2}\\right[`;
            }
        } else if (math.compare(delta, zero) === 0) { // Δ = 0
            return `\\mathbb{R}\\{${math.simplify(`-${this.b} / (2 * ${this.a})`).toString()}\\}`;
        } else { // Δ < 0
            if (aNumber > 0) {
                return `\\mathbb{R}`;
            } else {
                return `\\varnothing`;
            }
        }
    }
}

if (typeof window !== 'undefined') {
    (window as any).Trinome = Trinome;
}

export default Trinome;
